import { Content, ContentType, Lesson } from "../../types";
import { useCallback, useMemo } from "react";
import { useCourseContent } from "../../hooks/useCourseContent";
import { useAnalytics, Events, useUser } from "@hooks";
import { isHomeEDApp } from "@src/constants";
import {
  resolveMixpanelProgramName,
  resolveMixpanelUnitGrade,
  mixpanelMultiCollectionProps,
} from "@src/utils/mixpanelProgramType";
import { resolveUnitMixpanelLessonType } from "@src/utils/mixpanelLessonType";
import { mixpanelCompetencyProps } from "@src/utils/mixpanelCompetency";
import { mixpanelSkillProps } from "@src/utils/mixpanelSkill";
import { mixpanelMoodProps } from "@src/utils/mixpanelMood";
type ContentElements = {
  contentId: string;
  lessonId?: string;
  content: Content;
  lesson?: Lesson;
};
type ContentArr = {
  [lessonId: string]: ContentElements[];
};

function selectAdditionContent(modules: any[]) {
  if (modules.length > 0) {
    if (
      modules.at(0).content.type === ContentType.FORM ||
      modules.at(0).content.name.toLowerCase().includes("exit ticket")
    ) {
      modules.shift();
      return selectAdditionContent(modules);
    } else {
      return modules.at(0);
    }
  } else {
    return undefined;
  }
}

export const useNextContent = () => {
  const {
    section,
    startedAlternateLesson,
    content,
    currentUnit,
    currentHeaUnit,
    lesson,
    program,
    setLesson,
    setContent,
    setSelectionLevel,
    isParentApp,
    isPreview,
    isConfirmView,
    isStudyGuide,
    isStudentQuiz,
    studentQuiz,
    isNewLessonLayout,
  } = useCourseContent();
  const { trackEvent } = useAnalytics();
  const [user] = useUser();

  const getUnitModuleTrackProps = (next: ContentElements) => ({
    course_name: program?.name,
    grade: resolveMixpanelUnitGrade({ courseName: program?.name }),
    ...mixpanelCompetencyProps(currentUnit?.competency),
    ...mixpanelSkillProps({ skill: currentUnit?.skill }),
    ...mixpanelMoodProps({ categories: currentUnit?.categories }),
    ...mixpanelMultiCollectionProps(),
    unit_name: currentUnit?.name,
    lesson_name: next.lesson?.name,
    module_name: next.content?.name,
    program_name: resolveMixpanelProgramName({
      courseName: program?.name,
      unitName: currentUnit?.name,
      unitId: currentUnit?.id,
      unitUniqueName: currentUnit?.uniqueName,
    }),
    lessonType: resolveUnitMixpanelLessonType({
      unitName: currentUnit?.name,
      unitId: currentUnit?.id,
      unitUniqueName: currentUnit?.uniqueName,
    }),
    type: "Unit",
    schoolId: user?.schools?.[0]?.id,
    schoolName: user?.schools?.[0]?.name,
    userId: user?.id,
    userName: `${user?.firstName} ${user?.lastName}`,
  });

  const contentArray: ContentArr = useMemo(() => {
    const res: ContentArr = {};
    if (isConfirmView) {
      if (lesson?.id) {
        let modules = [...(lesson?.modules || [])];
        if (isPreview && !isNewLessonLayout) {
          modules = modules.filter((module) => module.lastInLesson !== true);
        }
        modules.forEach((c) => {
          if (!res[lesson?.id]) {
            res[lesson?.id] = [];
          }
          res[lesson?.id].push({
            contentId: c?.id || "",
            lessonId: lesson.id,
            content: c as Content,
            lesson,
          });
        });
      }
      return res;
    } else if (isStudentQuiz && studentQuiz?.contents) {
      let modules = [...(studentQuiz?.contents || [])];
      let addedFormAlready = false;
      modules.forEach((c) => {
        if (!res["squiz"]) {
          res["squiz"] = [];
        }
        if (isPreview) {
          res["squiz"].push({
            contentId: c?.id || "",
            lessonId: undefined,
            content: c as Content,
            lesson,
          });
        } else {
          if (!addedFormAlready) {
            res["squiz"].push({
              contentId: c?.id || "",
              lessonId: undefined,
              content: c as Content,
              lesson,
            });
            if (c.type === ContentType.FORM) {
              addedFormAlready = true;
            }
          }
        }
      });
      return res;
    }
    if (section && startedAlternateLesson) {
      if (section?.id) {
        let modules = [...(section?.modules || [])];
        if (isPreview && !isNewLessonLayout) {
          modules = modules.filter((module) => module.lastInLesson !== true);
        }
        modules.forEach((c) => {
          if (!res[section?.id]) {
            res[section?.id] = [];
          }
          res[section?.id].push({
            contentId: c?.id || "",
            lessonId: section.id,
            content: c as Content,
            lesson,
          });
        });
      }
      return res;
    }
    if (!currentUnit && !currentHeaUnit) return res;
    if (lesson) {
      (currentUnit?.classPresentation || []).forEach((l) => {
        if (l?.id === lesson.id) {
          let modules = [...(l?.modules || [])];
          if (isPreview && !isNewLessonLayout) {
            modules = modules.filter((module) => module.lastInLesson !== true);
          }
          modules.forEach((c) => {
            if (!res[l?.id]) {
              res[l?.id] = [];
            }
            res[l?.id].push({
              contentId: c?.id || "",
              lessonId: lesson.id,
              content: c as Content,
              lesson,
            });
          });
        }
      });
      if (isHomeEDApp) {
        (currentHeaUnit?.classPresentation || []).forEach((l) => {
          if (l?.id) {
            let modules = [...(l?.modules || [])];
            if (isPreview && !isNewLessonLayout) {
              modules = modules.filter(
                (module) => module.lastInLesson !== true,
              );
            }
            // if (isHomeEDApp) {// Survey comment out
            //   modules = modules.filter(module => module.type !== ContentType.FORM);
            // }
            modules.forEach((c) => {
              if (!res[l?.id]) {
                res[l?.id] = [];
              }
              res[l?.id].push({
                contentId: c?.id || "",
                lessonId: lesson.id,
                content: c as Content,
                lesson,
              });
            });
          }
        });
      }
    } else if (isStudyGuide) {
      if (currentUnit?.studyGuides) {
        (currentUnit?.studyGuides || []).forEach((c) => {
          if (!res["sg"]) {
            res["sg"] = [];
          }
          res["sg"].push({
            contentId: c?.id || "",
            lessonId: undefined,
            content: c as Content,
            lesson: undefined,
          });
        });
      }
    } else {
      (currentUnit?.teacherPreparation || []).forEach((c) => {
        if (!res["tp"]) {
          res["tp"] = [];
        }
        res["tp"].push({
          contentId: c?.id || "",
          lessonId: undefined,
          content: c as Content,
          lesson: undefined,
        });
      });
      if (isHomeEDApp) {
        (currentHeaUnit?.parentPreparation || []).forEach((c) => {
          if (!res["tp"]) {
            res["tp"] = [];
          }
          res["tp"].push({
            contentId: c?.id || "",
            lessonId: undefined,
            content: c as Content,
            lesson: undefined,
          });
        });
      }
    }
    return res;
  }, [
    currentHeaUnit,
    currentUnit,
    lesson,
    isPreview,
    isNewLessonLayout,
    isConfirmView,
    section,
    startedAlternateLesson,
    isStudyGuide,
    isStudentQuiz,
    studentQuiz,
  ]);

  const nextContent = useMemo(() => {
    let arr: ContentElements[] = [];
    if (section && startedAlternateLesson) {
      if (!contentArray || !content) return undefined;
      arr = contentArray[section.id];
    } else if (lesson) {
      if (!contentArray || !content) return undefined;
      arr = contentArray[lesson.id];
    } else if (isStudyGuide) {
      if (!contentArray || !content) return undefined;
      arr = contentArray["sg"];
    } else if (isStudentQuiz) {
      if (!contentArray || !content) return undefined;
      arr = contentArray["squiz"];
    } else {
      if (!contentArray || !content) return undefined;
      arr = contentArray["tp"];
    }
    if (!arr || !content) return undefined;
    const elIndex = arr.findIndex((c) => c.contentId === content.id);
    if (elIndex === -1) {
      return undefined;
    }
    return elIndex === arr.length - 1 ? undefined : arr[elIndex]?.content;
  }, [
    contentArray,
    lesson,
    content,
    section,
    startedAlternateLesson,
    isStudyGuide,
    isStudentQuiz,
  ]);

  const contentNext = useMemo(() => {
    if (!contentArray || !content) return undefined;
    if (!lesson) {
      return {
        lessonId: "",
        moduleId: "",
        previousModuleId: undefined,
        unitId: currentUnit?.id || "",
      };
    }
    let arr: ContentElements[] = [];
    if (section && startedAlternateLesson) {
      arr = contentArray[section.id];
    } else {
      arr = contentArray[lesson.id];
    }
    if (!arr || !content) return undefined;
    const elIndex = arr.findIndex((c) => c.contentId === content.id);
    return arr[elIndex + 1]
      ? {
          lessonId: arr[elIndex + 1].lessonId || "",
          moduleId: arr[elIndex + 1].contentId || "",
          previousModuleId: content.id,
          unitId: currentUnit?.id || "",
          sectionId: section?.id,
        }
      : undefined;
  }, [
    contentArray,
    lesson,
    content,
    currentUnit?.id,
    section,
    startedAlternateLesson,
  ]);

  const hasNextContent = !!nextContent;

  const hasPrevContent = useMemo(() => {
    let arr: ContentElements[] = [];
    if (section && startedAlternateLesson) {
      if (!contentArray || !content) return false;
      arr = contentArray[section.id];
    } else if (lesson) {
      if (!contentArray || !content) return false;
      arr = contentArray[lesson.id];
    } else if (isStudyGuide) {
      if (!contentArray || !content) return false;
      arr = contentArray["sg"];
    } else if (isStudentQuiz) {
      if (!contentArray || !content) return undefined;
      arr = contentArray["squiz"];
    } else {
      if (!contentArray || !content) return false;
      arr = contentArray["tp"];
    }
    if (!arr || !content) return false;
    const elIndex = arr.findIndex((c) => c.contentId === content.id);
    return elIndex > 0;
  }, [
    contentArray,
    lesson,
    content,
    section,
    startedAlternateLesson,
    isStudyGuide,
    isStudentQuiz,
  ]);

  const hasAdditionalContent = useMemo(() => {
    let arr: ContentElements[] = [];
    if (lesson) {
      if (!contentArray || !content) return false;
      arr = contentArray[lesson.id];
    } else if (isStudyGuide) {
      if (!contentArray || !content) return false;
      arr = contentArray["sg"];
    } else if (isStudentQuiz) {
      if (!contentArray || !content) return undefined;
      arr = contentArray["squiz"];
    } else {
      if (!contentArray || !content) return false;
      arr = contentArray["tp"];
    }
    if (!arr || !content) return false;

    const currentIndex = arr.findIndex((c) => c.contentId === content?.id) + 1;
    const remainModules = arr.slice(currentIndex);
    var addition = selectAdditionContent(remainModules);

    if (!addition) return false;

    if (addition.content.lastInLesson !== true) {
      return true;
    }
    return false;
  }, [contentArray, lesson, content, isStudyGuide, isStudentQuiz]);

  const selectNext = useCallback(
    (currentContent?: Content) => {
      const contentObj = currentContent || content;
      if (!contentObj) return;
      let arr: ContentElements[] = [];
      if (section && startedAlternateLesson) {
        arr = contentArray[section.id];
      } else if (lesson) {
        arr = contentArray[lesson.id];
      } else if (isStudyGuide) {
        arr = contentArray["sg"];
      } else if (isStudentQuiz) {
        arr = contentArray["squiz"];
      } else {
        arr = contentArray["tp"];
      }

      if (!arr) return false;
      const currentIndex =
        arr.findIndex((c) => c.contentId === contentObj.id) + 1;
      const next = arr[currentIndex];
      if (!!next) {
        setContent(next.content);
        setLesson(next.lesson);
        setSelectionLevel("Content");

        if (isPreview) {
          trackEvent(Events.actions.previewLessonModulesViewed, {
            ...getUnitModuleTrackProps(next),
          });
        } else {
          trackEvent(Events.actions.lessonModulesViewed, {
            ...getUnitModuleTrackProps(next),
          });
        }
      }
    },
    [
      content,
      lesson,
      contentArray,
      setContent,
      setLesson,
      setSelectionLevel,
      trackEvent,
      program?.name,
      currentUnit?.name,
      currentUnit?.id,
      currentUnit?.uniqueName,
      currentUnit?.competency,
      currentUnit?.skill,
      isPreview,
      section,
      startedAlternateLesson,
      isStudyGuide,
      isStudentQuiz,
      user,
    ],
  );

  const selectExitTicket = useCallback(
    (currentContent?: Content) => {
      const contentObj = currentContent || content;
      if (!contentObj) return;
      let arr: ContentElements[] = [];
      if (lesson) {
        arr = contentArray[lesson.id];
      } else if (isStudyGuide) {
        arr = contentArray["sg"];
      } else if (isStudentQuiz) {
        arr = contentArray["squiz"];
      } else {
        arr = contentArray["tp"];
      }

      if (!arr) return false;
      const exitTicketOrFaithIndex = arr.findIndex(
        (c) =>
          (c.content.type === ContentType.FORM ||
            c.content.name.toLowerCase().includes("exit ticket") ||
            c.content.name.toLowerCase().includes("faith perspective")) &&
          c.content?.position &&
          (!contentObj?.position || c.content.position > contentObj.position),
      );
      const next = arr[exitTicketOrFaithIndex];

      if (!!next) {
        setContent(next.content);
        setLesson(next.lesson);
        setSelectionLevel("Content");

        if (isPreview) {
          trackEvent(Events.actions.previewLessonModulesViewed, {
            ...getUnitModuleTrackProps(next),
          });
        } else {
          trackEvent(Events.actions.lessonModulesViewed, {
            ...getUnitModuleTrackProps(next),
          });
        }
      }
    },
    [
      content,
      lesson,
      contentArray,
      setContent,
      setLesson,
      setSelectionLevel,
      trackEvent,
      program?.name,
      currentUnit?.name,
      currentUnit?.id,
      currentUnit?.uniqueName,
      currentUnit?.competency,
      currentUnit?.skill,
      isPreview,
      isStudyGuide,
      isStudentQuiz,
      user,
    ],
  );

  const selectAdditionalContent = useCallback(
    (currentContent?: Content) => {
      const contentObj = currentContent || content;
      if (!contentObj) return;
      let arr: ContentElements[] = [];
      if (lesson) {
        arr = contentArray[lesson.id];
      } else if (isStudyGuide) {
        arr = contentArray["sg"];
      } else if (isStudentQuiz) {
        arr = contentArray["squiz"];
      } else {
        arr = contentArray["tp"];
      }

      if (!arr) return false;
      const currentIndex =
        arr.findIndex((c) => c.contentId === contentObj.id) + 1;
      const remainModules = arr.slice(currentIndex);
      var next = selectAdditionContent(remainModules);
      if (!!next) {
        setContent(next.content);
        setLesson(next.lesson);
        setSelectionLevel("Content");

        if (isPreview) {
          trackEvent(Events.actions.previewLessonModulesViewed, {
            ...getUnitModuleTrackProps(next),
          });
        } else {
          trackEvent(Events.actions.lessonModulesViewed, {
            ...getUnitModuleTrackProps(next),
          });
        }
      }
    },
    [
      content,
      lesson,
      contentArray,
      setContent,
      setLesson,
      setSelectionLevel,
      trackEvent,
      program?.name,
      currentUnit?.name,
      currentUnit?.id,
      currentUnit?.uniqueName,
      currentUnit?.competency,
      currentUnit?.skill,
      isPreview,
      isStudyGuide,
      isStudentQuiz,
      user,
    ],
  );

  const selectPrev = useCallback(() => {
    if (!content) return;
    let arr: ContentElements[] = [];
    if (section && startedAlternateLesson) {
      arr = contentArray[section.id];
    } else if (lesson) {
      arr = contentArray[lesson.id];
    } else if (isStudyGuide) {
      arr = contentArray["sg"];
    } else if (isStudentQuiz) {
      arr = contentArray["squiz"];
    } else {
      arr = contentArray["tp"];
    }
    if (!arr || !content) return false;
    const currentIndex = arr.findIndex((c) => c.contentId === content.id);
    const next = arr[currentIndex - 1];
    if (next) {
      setContent(next.content);
      setLesson(next.lesson);
      setSelectionLevel("Content");
      if (isPreview) {
        trackEvent(Events.actions.previewLessonModulesViewed, {
          ...getUnitModuleTrackProps(next),
        });
      } else {
        trackEvent(Events.actions.lessonModulesViewed, {
          ...getUnitModuleTrackProps(next),
        });
      }
    }
  }, [
    content,
    lesson,
    contentArray,
    setContent,
    setLesson,
    setSelectionLevel,
    trackEvent,
    program?.name,
    currentUnit?.name,
    currentUnit?.id,
    currentUnit?.uniqueName,
    currentUnit?.competency,
    currentUnit?.skill,
    isPreview,
    section,
    startedAlternateLesson,
    isStudyGuide,
    isStudentQuiz,
    user,
  ]);

  return {
    hasNextContent,
    hasPrevContent,
    hasAdditionalContent,
    selectNext,
    selectPrev,
    selectExitTicket,
    selectAdditionalContent,
    nextContent,
    contentNext,
    isParentApp,
    content,
  };
};
